import React, { useEffect, useState } from "react";
import { Trophy, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAME_KEY = "jthub_ear_name";

export const EarLeaderboard = ({ level, levelLabel, sessionBest, correct }) => {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || user?.name?.split(" ")[0] || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/ear-trainer/leaderboard", { params: { level } }).then(({ data }) => setBoard(data)).catch(() => {}); }, [level]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Enter a name for the leaderboard");
    setBusy(true);
    try {
      localStorage.setItem(NAME_KEY, name.trim());
      const { data } = await api.post("/ear-trainer/leaderboard", { name: name.trim(), level, streak: sessionBest, correct });
      setBoard(data);
      toast.success(data.my_rank ? `You're #${data.my_rank} this week!` : "Score submitted");
    } catch { toast.error("Could not submit score"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-8 rounded-3xl border border-border bg-card p-6" data-testid="ear-leaderboard">
      <div className="flex flex-wrap items-center gap-2">
        <Trophy size={18} className="text-primary" />
        <h2 className="font-serif text-xl">This week's leaderboard · {levelLabel}</h2>
        {board && <span className="ml-auto font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{board.week}</span>}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Ranked by best streak, resets every Monday. Log in and finish the week at #1 to win a prize badge — 10 badges = a half-price 1:1 with Jason.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Your name" className="max-w-xs border-border bg-background" data-testid="ear-leaderboard-name" />
        <Button onClick={submit} disabled={busy || sessionBest === 0} className="rounded-full bg-primary text-primary-foreground" data-testid="ear-leaderboard-submit">
          <Send size={14} className="mr-2" /> Submit best streak ({sessionBest})
        </Button>
      </div>
      <ol className="mt-5 divide-y divide-border" data-testid="ear-leaderboard-list">
        {!board?.entries?.length ? <li className="py-3 text-sm text-muted-foreground" data-testid="ear-leaderboard-empty">No scores yet this week — be the first!</li> : board.entries.map((e) => (
          <li key={e.rank} className={`flex items-center gap-3 py-2.5 text-sm ${e.is_me ? "text-primary" : ""}`} data-testid={`ear-leaderboard-row-${e.rank}`}>
            <span className="w-6 font-mono text-muted-foreground">{e.rank}</span>
            <span className="font-medium">{e.name}</span>
            <span className="ml-auto text-muted-foreground">streak {e.streak} · {e.correct} correct</span>
          </li>
        ))}
      </ol>
    </div>
  );
};
